import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Paperclip, Wallet, X } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Spinner,
} from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import {
  withdrawalBreakdown,
  type AdminWithdrawalItem,
  type AdminWithdrawalStatus,
} from '@pnl/shared';
import { api } from '../lib/api';

function fmtBrl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Baixa a nota fiscal (autenticada) e abre numa nova aba. */
async function openInvoice(id: string): Promise<void> {
  const blob = await api.getAdminWithdrawalInvoiceBlob(id);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Baixa o CSV de saques (respeitando o filtro de status) e dispara o download. */
async function downloadWithdrawalsCsv(status: AdminWithdrawalStatus | 'ALL'): Promise<void> {
  const blob = await api.downloadWithdrawalsCsv(status === 'ALL' ? undefined : status);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'saques.csv';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const statusTone: Record<
  AdminWithdrawalStatus,
  'neutral' | 'brand' | 'success' | 'warning' | 'danger'
> = {
  REQUESTED: 'warning',
  APPROVED: 'brand',
  PAID: 'success',
  REJECTED: 'danger',
  CANCELED: 'neutral',
};

const statusLabel: Record<AdminWithdrawalStatus, string> = {
  REQUESTED: 'Solicitado',
  APPROVED: 'Confirmado',
  PAID: 'Realizado',
  REJECTED: 'Negado',
  CANCELED: 'Cancelado',
};

const FILTERS: { value: AdminWithdrawalStatus | 'ALL'; label: string }[] = [
  { value: 'REQUESTED', label: 'Solicitados' },
  { value: 'APPROVED', label: 'Confirmados' },
  { value: 'PAID', label: 'Realizados' },
  { value: 'ALL', label: 'Todos' },
];

export function WithdrawalsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<AdminWithdrawalStatus | 'ALL'>('REQUESTED');
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'withdrawals', filter],
    queryFn: () => api.listAdminWithdrawals(filter === 'ALL' ? {} : { status: filter }),
  });

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['admin', 'withdrawals'] });
  }

  const approve = useMutation({
    mutationFn: (id: string) => api.approveWithdrawal(id),
    onSuccess: refresh,
    onError: (e) => setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao aprovar'),
  });
  const pay = useMutation({
    mutationFn: (id: string) => api.markWithdrawalPaid(id),
    onSuccess: refresh,
    onError: (e) =>
      setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao marcar pago'),
  });
  const reject = useMutation({
    mutationFn: (id: string) => api.rejectWithdrawal(id, { reason: reason.trim() }),
    onSuccess: () => {
      setRejectId(null);
      setReason('');
      refresh();
    },
    onError: (e) => setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao rejeitar'),
  });

  async function handleExportCsv() {
    setActionError(null);
    try {
      await downloadWithdrawalsCsv(filter);
    } catch (e) {
      setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao exportar o CSV');
    }
  }

  async function handleOpenInvoice(id: string) {
    setActionError(null);
    try {
      await openInvoice(id);
    } catch (e) {
      setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao abrir a nota fiscal');
    }
  }

  const items = query.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">Operação</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Saques
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Fluxo: Solicitado → Confirmar saque (debita o saldo e libera o envio da nota fiscal pelo
          trader) → conferir a NF e pagar via Pix → Saque realizado. O trader é notificado a cada
          etapa.
        </p>
      </div>

      {actionError && <Alert tone="danger">{actionError}</Alert>}

      <Card>
        <CardHeader>
          <div className="flex w-full items-center justify-between gap-4">
            <CardTitle>
              {items.length} saque(s){query.isLoading ? '…' : ''}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  void handleExportCsv();
                }}
              >
                Exportar CSV
              </Button>
              <div className="flex gap-1">
                {FILTERS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setFilter(f.value)}
                    className={
                      'rounded-md px-3 py-1.5 text-xs font-medium transition-colors ' +
                      (filter === f.value
                        ? 'bg-brand-500 text-white'
                        : 'text-ink-500 hover:bg-ink-100')
                    }
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Wallet size={28} />}
              title="Nenhum saque nesta categoria"
              description="Solicitações de saque dos traders aparecem aqui para auditoria."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-ink-100 text-2xs text-ink-500 border-b text-left font-semibold uppercase tracking-widest">
                    <th className="px-3 py-2.5">Trader / Conta</th>
                    <th className="px-2 py-2.5 text-right">Bruto</th>
                    <th className="px-2 py-2.5 text-right">Líquido (−20% −20%)</th>
                    <th className="px-2 py-2.5">Pix</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((w) => (
                    <WithdrawalRow
                      key={w.id}
                      w={w}
                      onApprove={() => {
                        setActionError(null);
                        approve.mutate(w.id);
                      }}
                      onPay={() => {
                        setActionError(null);
                        pay.mutate(w.id);
                      }}
                      onReject={() => {
                        setActionError(null);
                        setReason('');
                        setRejectId(w.id);
                      }}
                      onOpenInvoice={() => void handleOpenInvoice(w.id)}
                      busy={
                        (approve.isPending && approve.variables === w.id) ||
                        (pay.isPending && pay.variables === w.id)
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {rejectId && (
        <div
          className="bg-ink-900/40 fixed inset-0 z-40 flex items-center justify-center p-4"
          role="presentation"
          onClick={() => setRejectId(null)}
        >
          <div
            className="border-ink-200 shadow-lift w-full max-w-md rounded-xl border bg-white p-6"
            role="dialog"
            aria-label="Rejeitar saque"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-ink-900 text-lg font-semibold">Rejeitar saque</h2>
            <p className="text-ink-500 mt-1 text-sm">
              O trader é notificado com o motivo. O saldo não é alterado.
            </p>
            <div className="mt-4">
              <Field label="Motivo" htmlFor="reject-reason" required>
                <Input
                  id="reject-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex.: fora da janela / inconsistência detectada"
                />
              </Field>
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                size="sm"
                variant="danger"
                loading={reject.isPending}
                onClick={() => reject.mutate(rejectId)}
              >
                Confirmar rejeição
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRejectId(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WithdrawalRow({
  w,
  onApprove,
  onPay,
  onReject,
  onOpenInvoice,
  busy,
}: {
  w: AdminWithdrawalItem;
  onApprove: () => void;
  onPay: () => void;
  onReject: () => void;
  onOpenInvoice: () => void;
  busy: boolean;
}) {
  const b = withdrawalBreakdown(w.amountCents);
  return (
    <tr className="border-ink-100 border-b">
      <td className="px-3 py-2.5">
        <p className="text-ink-900 font-medium">{w.traderName ?? w.traderEmail}</p>
        <p className="text-ink-500 text-xs">
          {w.accountLabel} · {w.marketCode} · {new Date(w.requestedAt).toLocaleDateString('pt-BR')}
        </p>
        {w.invoiceFileName ? (
          <button
            type="button"
            onClick={onOpenInvoice}
            className="text-brand-600 hover:text-brand-700 mt-1 inline-flex items-center gap-1 text-xs font-semibold"
          >
            <Paperclip size={11} /> Nota fiscal
          </button>
        ) : (
          <span className="text-ink-400 mt-1 inline-flex items-center gap-1 text-xs">
            <Paperclip size={11} /> Sem nota fiscal
          </span>
        )}
      </td>
      <td className="text-ink-900 px-2 py-2.5 text-right font-semibold tabular-nums">
        {fmtBrl(w.amountCents)}
      </td>
      <td className="text-ink-700 px-2 py-2.5 text-right text-xs tabular-nums">
        <span className="text-success-700 font-semibold">{fmtBrl(b.netCents)}</span>
        <br />
        <span className="text-ink-400">
          mesa {fmtBrl(b.mesaFeeCents)} · IR {fmtBrl(b.irCents)}
        </span>
      </td>
      <td className="text-ink-600 px-2 py-2.5 text-xs">{w.pixKey ?? '—'}</td>
      <td className="px-3 py-2.5">
        <Badge tone={statusTone[w.status]}>{statusLabel[w.status]}</Badge>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex justify-end gap-2">
          {w.status === 'REQUESTED' && (
            <>
              <Button
                size="sm"
                leadingIcon={<Check size={13} />}
                loading={busy}
                onClick={onApprove}
              >
                Confirmar saque
              </Button>
              <Button
                size="sm"
                variant="secondary"
                leadingIcon={<X size={13} />}
                onClick={onReject}
              >
                Rejeitar
              </Button>
            </>
          )}
          {w.status === 'APPROVED' && (
            <Button size="sm" loading={busy} onClick={onPay}>
              Saque realizado
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
